import React, { useEffect, useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import type { TrackChangesPlugin } from '@atlaskit/editor-plugins/track-changes';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type { Suggestion } from '../StagingArea';

import type { usePreviewFullPagePreset } from './usePreviewFullPagePreset';

export type EditorPreviewAPI = PublicPluginAPI<[AiStreamingOrchestratorPlugin, TrackChangesPlugin]>;

interface EditorPreviewProps {
	adf: DocNode;
	editorApi: ReturnType<typeof usePreviewFullPagePreset>['editorApi'];
	preset: ReturnType<typeof usePreviewFullPagePreset>['preset'];
	suggestions?: Suggestion[];
}

/**
 * An editor preview component using the Confluence full page preset.
 * Used to display AI-generated content suggestions.
 * The editor is editable by default after loading the dummy ADF.
 */
export function EditorPreview({ adf, preset, editorApi, suggestions }: EditorPreviewProps) {
	const applySuggestions = useCallback(() => {
		editorApi?.core.actions.execute(editorApi?.trackChanges?.commands?.toggleChanges);

		editorApi?.aiStreamingOrchestrator?.actions.startSession();
		suggestions?.forEach((suggestion) => {
			suggestion.tool_calls.forEach((toolCall) => {
				const id = editorApi?.aiStreamingOrchestrator?.actions.initAction(
					{
						type: toolCall.name,
						localId: toolCall.arguments.localId,
					} as StreamingCommand,
					{
						hideLoadingScreen: true,
					},
				);
				if (id) {
					editorApi?.aiStreamingOrchestrator?.actions.streamValue(id, toolCall.arguments.value);
					editorApi?.aiStreamingOrchestrator?.actions.finishAction(id, undefined);
				}
			});
		});
		editorApi?.aiStreamingOrchestrator?.actions.endSession();
	}, [editorApi, suggestions]);

	useEffect(() => {
		applySuggestions();
	}, [applySuggestions]);

	return (
		<>
			<EditorContext>
				<ComposableEditor preset={preset} appearance="full-page" defaultValue={adf} disabled />
			</EditorContext>
		</>
	);
}
